// ============================================================================
// CONFIG SETTINGS HOOK — central state for ConfigSettingsScreen
// ============================================================================
// Manages: groups, scope tabs (SYSTEM / ENTITY / PRODUCT), product picker,
// effective values, edit tracking (override / inherit), and save flow.
//
// Recsuit hierarchy: SYSTEM (level 0) → ENTITY (1) → PRODUCT (2). Single-
// entity tenants typically skip ENTITY — the Settings UI hides the Entity
// tab entirely when no ENTITY scope rows exist.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { configService } from '../../api/services/configService';
import { productService } from '../../api/services/productService';
import type {
  ConfigGroup,
  ConfigDefinition,
  ConfigEffectiveValue,
  ConfigScopeRef,
  ConfigScopeType,
  ConfigListItem,
  ConfigValueUpdate,
} from '../../api/types/config.types';

export type ScopeTab = 'SYSTEM' | 'ENTITY' | 'PRODUCT';

export interface EditedValue {
  /** scalar value; null/undefined means unchanged. To clear an override, see clearedKeys. */
  value?: string | null;
  /** list values for is_multi_value definitions */
  listValues?: ConfigListItem[];
}

export interface UseConfigSettingsState {
  // Groups
  groups: ConfigGroup[];
  groupsLoading: boolean;
  activeGroupKey: string | null;
  setActiveGroupKey: (key: string | null) => void;

  // Scope
  scopeType: ScopeTab;
  setScopeType: (type: ScopeTab) => void;
  selectedEntityId: string | null;
  setSelectedEntityId: (id: string | null) => void;
  selectedProductId: string | null;
  setSelectedProductId: (id: string | null) => void;

  // Definitions + values
  definitions: ConfigDefinition[];
  effectiveValues: ConfigEffectiveValue[];
  valuesLoading: boolean;

  // Edits in flight
  editedValues: Record<string, EditedValue>;
  clearedKeys: Set<string>;
  hasChanges: boolean;
  updateFieldValue: (configKey: string, value: string | null) => void;
  updateListValue: (configKey: string, items: ConfigListItem[]) => void;
  clearOverride: (configKey: string) => void;
  resetEdits: () => void;

  // Save / refresh
  saving: boolean;
  saveError: Error | null;
  saveChanges: () => Promise<boolean>;
  refresh: () => Promise<void>;

  // Product picker (loaded lazily — only when Product tab opens)
  productOptions: { value: string; label: string }[];
  productsLoading: boolean;

  // Hint flags for UI
  showEntityTab: boolean;
}

/** Build the scope-ref array used by /Config/effective and /Config/values. */
const buildScopes = (
  scopeType: ScopeTab,
  entityId: string | null,
  productId: string | null,
): ConfigScopeRef[] => {
  const refs: ConfigScopeRef[] = [];
  if (scopeType === 'ENTITY' && entityId) refs.push({ scopeType: 'ENTITY', scopeId: entityId });
  if (scopeType === 'PRODUCT') {
    if (entityId) refs.push({ scopeType: 'ENTITY', scopeId: entityId });
    if (productId) refs.push({ scopeType: 'PRODUCT', scopeId: productId });
  }
  return refs;
};

export const useConfigSettings = (): UseConfigSettingsState => {
  // ---- Groups ----
  const [groups, setGroups] = useState<ConfigGroup[]>([]);
  const [groupsLoading, setGroupsLoading] = useState(true);
  const [activeGroupKey, setActiveGroupKey] = useState<string | null>(null);

  // ---- Scope ----
  const [scopeType, setScopeType] = useState<ScopeTab>('SYSTEM');
  const [selectedEntityId, setSelectedEntityId] = useState<string | null>(null);
  const [selectedProductId, setSelectedProductId] = useState<string | null>(null);

  // ---- Definitions + values ----
  const [definitions, setDefinitions] = useState<ConfigDefinition[]>([]);
  const [effectiveValues, setEffectiveValues] = useState<ConfigEffectiveValue[]>([]);
  const [valuesLoading, setValuesLoading] = useState(false);

  // ---- Edits ----
  const [editedValues, setEditedValues] = useState<Record<string, EditedValue>>({});
  const [clearedKeys, setClearedKeys] = useState<Set<string>>(new Set());

  // ---- Save ----
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<Error | null>(null);

  // ---- Products (lazy-loaded for Product tab) ----
  const [productOptions, setProductOptions] = useState<{ value: string; label: string }[]>([]);
  const [productsLoading, setProductsLoading] = useState(false);
  const productsLoadedRef = useRef(false);

  // ---- Entity tab visibility (true if any ENTITY scope rows exist) ----
  const [showEntityTab, setShowEntityTab] = useState(false);

  // -------- INIT --------

  // Load groups + entity-count check on mount
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setGroupsLoading(true);
      try {
        const [groupsRes, entityScopesRes] = await Promise.all([
          configService.getGroups(),
          configService.getScopesByType('ENTITY').catch(() => null),
        ]);
        if (cancelled) return;
        const list = groupsRes.data ?? [];
        setGroups(list);
        if (list.length > 0) setActiveGroupKey(list[0].groupKey);

        // Show ENTITY tab only if at least one ENTITY scope row exists. For
        // single-entity tenants this stays hidden — they only see SYSTEM and
        // PRODUCT tabs.
        const entityScopes = entityScopesRes?.data ?? [];
        setShowEntityTab(entityScopes.length > 0);
      } catch {
        if (!cancelled) setGroups([]);
      } finally {
        if (!cancelled) setGroupsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Load products lazily the first time the user picks the Product tab
  const ensureProductsLoaded = useCallback(async () => {
    if (productsLoadedRef.current) return;
    productsLoadedRef.current = true;
    setProductsLoading(true);
    try {
      // API caps pageSize at 100; recsuit has fewer than 100 products in practice.
      const res = await productService.getAll({ page: 1, pageSize: 100 });
      setProductOptions(
        res.data.map(p => ({
          value: p.id,
          label: `${p.productName} (${p.productCode})`,
        }))
      );
    } catch {
      setProductOptions([]);
      productsLoadedRef.current = false; // allow retry
    } finally {
      setProductsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (scopeType === 'PRODUCT') ensureProductsLoaded();
  }, [scopeType, ensureProductsLoaded]);

  // -------- LOAD DEFINITIONS + EFFECTIVE VALUES --------

  const loadGroupAndScope = useCallback(async (
    groupKey: string,
    activeScope: ScopeTab,
    entityId: string | null,
    productId: string | null,
  ) => {
    if (!groupKey) {
      setDefinitions([]);
      setEffectiveValues([]);
      return;
    }
    setValuesLoading(true);
    try {
      const [defsRes, effRes] = await Promise.all([
        configService.getDefinitions(groupKey),
        configService.getEffective(groupKey, buildScopes(activeScope, entityId, productId)),
      ]);
      setDefinitions(defsRes.data ?? []);
      setEffectiveValues(effRes.data ?? []);
    } catch {
      setDefinitions([]);
      setEffectiveValues([]);
    } finally {
      setValuesLoading(false);
    }
  }, []);

  // Re-fetch on group / scope / entity / product change. Edits are dropped on
  // any context change — caller is responsible for prompting first.
  useEffect(() => {
    if (!activeGroupKey) return;
    setEditedValues({});
    setClearedKeys(new Set());
    // PRODUCT scope can't load until both product and (optionally) entity are picked.
    if (scopeType === 'PRODUCT' && !selectedProductId) {
      setDefinitions([]);
      setEffectiveValues([]);
      return;
    }
    if (scopeType === 'ENTITY' && !selectedEntityId) {
      setDefinitions([]);
      setEffectiveValues([]);
      return;
    }
    loadGroupAndScope(activeGroupKey, scopeType, selectedEntityId, selectedProductId);
  }, [activeGroupKey, scopeType, selectedEntityId, selectedProductId, loadGroupAndScope]);

  // -------- EDIT TRACKING --------

  const updateFieldValue = useCallback((configKey: string, value: string | null) => {
    setEditedValues(prev => ({
      ...prev,
      [configKey]: { ...(prev[configKey] ?? {}), value },
    }));
    setClearedKeys(prev => {
      if (!prev.has(configKey)) return prev;
      const n = new Set(prev); n.delete(configKey); return n;
    });
  }, []);

  const updateListValue = useCallback((configKey: string, items: ConfigListItem[]) => {
    setEditedValues(prev => ({
      ...prev,
      [configKey]: { ...(prev[configKey] ?? {}), listValues: items },
    }));
    setClearedKeys(prev => {
      if (!prev.has(configKey)) return prev;
      const n = new Set(prev); n.delete(configKey); return n;
    });
  }, []);

  const clearOverride = useCallback((configKey: string) => {
    setClearedKeys(prev => {
      const n = new Set(prev); n.add(configKey); return n;
    });
    setEditedValues(prev => {
      if (!prev[configKey]) return prev;
      const n = { ...prev }; delete n[configKey]; return n;
    });
  }, []);

  const resetEdits = useCallback(() => {
    setEditedValues({});
    setClearedKeys(new Set());
  }, []);

  const hasChanges = useMemo(() => {
    return Object.keys(editedValues).length > 0 || clearedKeys.size > 0;
  }, [editedValues, clearedKeys]);

  // -------- SAVE --------

  const saveChanges = useCallback(async (): Promise<boolean> => {
    if (!hasChanges) return true;

    // Determine target scope. SYSTEM saves to scope_id NULL; ENTITY/PRODUCT
    // require selected ID.
    let scopeId: string | null = null;
    if (scopeType === 'ENTITY') {
      if (!selectedEntityId) return false;
      scopeId = selectedEntityId;
    } else if (scopeType === 'PRODUCT') {
      if (!selectedProductId) return false;
      scopeId = selectedProductId;
    }

    // Build the value-update payload.
    const updates: ConfigValueUpdate[] = [];
    for (const [configKey, edit] of Object.entries(editedValues)) {
      const def = definitions.find(d => d.configKey === configKey);
      if (!def) continue;
      if (def.isMultiValue) {
        updates.push({ configKey, listValues: edit.listValues ?? [] });
      } else {
        updates.push({ configKey, value: edit.value ?? null });
      }
    }
    for (const configKey of clearedKeys) {
      const def = definitions.find(d => d.configKey === configKey);
      if (!def) continue;
      // null payload signals "remove the override at this scope".
      if (def.isMultiValue) {
        updates.push({ configKey, listValues: null });
      } else {
        updates.push({ configKey, value: null });
      }
    }

    if (updates.length === 0) return true;

    setSaving(true);
    setSaveError(null);
    try {
      await configService.updateValues({ scopeType, scopeId, values: updates });
      // Reload effective values to reflect server state
      if (activeGroupKey) {
        await loadGroupAndScope(activeGroupKey, scopeType, selectedEntityId, selectedProductId);
      }
      setEditedValues({});
      setClearedKeys(new Set());
      return true;
    } catch (err) {
      setSaveError(err as Error);
      return false;
    } finally {
      setSaving(false);
    }
  }, [hasChanges, scopeType, selectedEntityId, selectedProductId, editedValues, clearedKeys, definitions, activeGroupKey, loadGroupAndScope]);

  const refresh = useCallback(async () => {
    if (!activeGroupKey) return;
    await loadGroupAndScope(activeGroupKey, scopeType, selectedEntityId, selectedProductId);
  }, [activeGroupKey, scopeType, selectedEntityId, selectedProductId, loadGroupAndScope]);

  return {
    groups, groupsLoading, activeGroupKey, setActiveGroupKey,
    scopeType, setScopeType,
    selectedEntityId, setSelectedEntityId,
    selectedProductId, setSelectedProductId,
    definitions, effectiveValues, valuesLoading,
    editedValues, clearedKeys, hasChanges,
    updateFieldValue, updateListValue, clearOverride, resetEdits,
    saving, saveError, saveChanges, refresh,
    productOptions, productsLoading,
    showEntityTab,
  };
};

export default useConfigSettings;
